import { useRef } from "react";
import { ArrowRight, CalendarDays, Film, MapPin, Scissors, Sparkles, X } from "lucide-react";
import Footer from "@/components/ui/Footer";
import { fonts } from "@/lib/fonts";
import "./StitchesNightOut.css";

const poster = "/events/practical-magic-2.jpg";

export function StitchesNightOut() {
  const posterDialog = useRef<HTMLDialogElement>(null);

  return (
    <>
      <main className="sno-page" style={{ fontFamily: fonts.body }}>
        <section className="sno-candlelight sno-hero">
          <div className="sno-hero-inner">
            <div className="sno-hero-copy">
              <h1 className="sno-glow" style={{ fontFamily: fonts.logo }}>Stitches<br /><span className="sno-magic-word">Night Out<span className="sno-glint" aria-hidden="true">✦</span></span></h1>
              <p className="sno-hero-description">Make new friends, work on your hand crafts, win prizes, and enjoy <em>Practical Magic 2!</em></p>
              <div className="sno-hero-logistics">
                <p className="sno-hero-when"><CalendarDays size={22} aria-hidden="true" /> October 20 · 6:30 PM</p>
                <div className="sno-hero-where">
                  <MapPin size={20} aria-hidden="true" />
                  <div><p className="sno-venue-name">American Fork Cinemark <a href="https://share.google/qDMdJ5wUp2MMLydVf" target="_blank" rel="noreferrer" className="sno-directions sno-venue-directions">Directions <ArrowRight size={14} aria-hidden="true" /></a></p><p className="sno-hero-address">715 West 180 North, American Fork, UT 84003</p></div>
                </div>
              </div>
              <p className="sno-booking-note" role="status">This event has ended. Thank you for joining us! Ticket sales are closed.</p>
            </div>
            <button type="button" onClick={() => posterDialog.current?.showModal()} className="sno-poster-link" aria-label="Expand Practical Magic 2 movie poster" aria-haspopup="dialog" aria-controls="sno-poster-modal">
              <img src={poster} alt="Practical Magic 2 movie poster featuring Sandra Bullock and Nicole Kidman in warm candlelight" width={1080} height={1350} className="sno-hero-poster" />
            </button>
          </div>
        </section>

        <dialog ref={posterDialog} id="sno-poster-modal" className="sno-poster-modal" aria-label="Practical Magic 2 movie poster" onClick={event => {
          if (event.target === event.currentTarget) posterDialog.current?.close();
        }}>
          <button type="button" className="sno-poster-close" aria-label="Close poster" onClick={() => posterDialog.current?.close()}><X size={24} aria-hidden="true" /></button>
          <img src={poster} alt="Full Practical Magic 2 movie poster featuring Sandra Bullock and Nicole Kidman" width={1080} height={1350} />
        </dialog>

        <section className="sno-details mx-auto max-w-6xl px-6 py-14 sm:py-16">
          <h2 className="text-4xl sm:text-5xl" style={{ fontFamily: fonts.logo }}>Your project. Your people. A little magic.</h2>
          <div className="mt-10 grid gap-8 sm:grid-cols-3">
            <div><Scissors className="mb-4" aria-hidden="true" /><h3 className="text-lg font-bold">Bring your handwork</h3><p className="mt-2 leading-relaxed">Pack your favorite hand project and enjoy an evening with fellow makers.</p></div>
            <div><Sparkles className="mb-4" aria-hidden="true" /><h3 className="text-lg font-bold">Find your crafty people</h3><p className="mt-2 leading-relaxed">Make new friends and join in the fun with a chance to win prizes.</p></div>
            <div><Film className="mb-4" aria-hidden="true" /><h3 className="text-lg font-bold">Make it a movie night</h3><p className="mt-2 leading-relaxed">Enjoy <em>Practical Magic 2</em> on the big screen at American Fork Cinemark.</p></div>
          </div>
        </section>

        <section className="sno-hosts px-6 py-10 text-center">
          <h2 className="text-3xl" style={{ fontFamily: fonts.logo }}>Hosted by</h2>
          <div className="mt-5 flex flex-wrap justify-center gap-x-6 gap-y-3 text-lg font-bold">
            <a className="underline underline-offset-4" href="https://www.instagram.com/chelsea_b_porter/" target="_blank" rel="noreferrer">Chelsea B. Porter</a>
            <span aria-hidden="true">&amp;</span>
            <a className="underline underline-offset-4" href="https://www.instagram.com/iguessimakethings/" target="_blank" rel="noreferrer">@iguessimakethings</a>
          </div>
        </section>

        <section className="mx-auto max-w-5xl px-6 py-14 text-center">
          <h2 className="text-3xl" style={{ fontFamily: fonts.logo }}>With thanks to our sponsors</h2>
          <ul className="sno-sponsors mt-8 flex flex-wrap items-center justify-center gap-x-12 gap-y-8">
            <li><img src="/events/sponsors/baby-lock.svg" alt="Baby Lock" className="sno-sponsor-babylock" /></li>
            <li><img src="/events/sponsors/cutterpillar.png" alt="Cutterpillar" width={400} height={69} className="sno-sponsor-cutterpillar" /></li>
            <li><img src="/events/sponsors/oliso.png" alt="Oliso" width={505} height={192} className="sno-sponsor-oliso" /></li>
            <li><img src="/events/sponsors/seamstress-for-the-band.jpg" alt="Seamstress for the Band Quilting Co." width={240} height={240} className="sno-sponsor-seamstress" /></li>
            <li><img src="/events/sponsors/quilt-101.png" alt="Quilt 101" width={835} height={167} className="sno-sponsor-quilt101" /></li>
            <li><img src="/events/sponsors/harmony.png" alt="Harmony" width={1219} height={180} className="sno-sponsor-harmony" /></li>
          </ul>
        </section>
      </main>
      <Footer dark />
    </>
  );
}
